/* global modForm modUtils Modalytics Promise */
/* eslint-disable camelcase */
import {
	switchFormLoadingState,
	getProjExUrl,
	createHeapSessionIdCookie,
	heapIdentify,
	getHashedData,
	normalizeEmail,
	normalizeName,
	normalizePhone
} from '../../helpers';
import { isQsSiteEligibleForHrc } from '../../validators';
/**
 * Process form after succesful submit
 * @param {Object} response - response data
 * @param {Object} xhr - xhr
 * @returns {Boolean} Submission status
 */
export async function processFormsServiceApiSubmitSuccess(response, xhr) {
	let cookieData;
	const responseData = response,
		dataCaptureKey = responseData.dataCaptureKey,
		zip = responseData.zip,
		isWholeSaleMatch = responseData.vendors?.length === 1 && responseData.vendors[0].wholeSale;

	// If submission was succesful
	if (dataCaptureKey !== null && xhr.status === 200) {
		const clickKeyUUID = modForm.opts.formData.CLK;

		// Setting GA User ID and several custom dimensions after a successful form submit request
		if (typeof dataLayer !== 'undefined') {
			window.dataLayer.push({
				event: 'formSubmit',
				userId: clickKeyUUID,
				lastProjectId: dataCaptureKey,
				projectId: dataCaptureKey,
				projectClass: modForm.opts.formData.service
			});
		}

		// Sending GA Event to keep track of all non-TYP conversions, including this one
		Modalytics.gaSend({
			hitType: 'event',
			eventCategory: 'HRC General',
			eventAction: 'User converted on non-TYP experience',
			eventLabel: window.location.pathname
		}, modForm.opts.gaTracker);

		// One last attempt at identifying the user before redirecting
		heapIdentify();

		// Alternate heap conversion event for Homeowner Lead Form Conversion combo event
		modForm.homeownerLeadConversionProps.zip = zip;
		Modalytics.heap('track', ['Homeowner Lead Conversion', modForm.homeownerLeadConversionProps]);

		// Hash and send PII to dataLayer
		const formData = modForm.opts.formData;
		// DO NOT MODIFY the user_data structure; it follows Google's documentation.
		let google_user_data, fb_user_data;

		try {
			const [fbHashedEmail, fbHashedPhone] = await Promise.all([
				getHashedData(normalizeEmail(formData.email, false)),
				getHashedData(normalizePhone(formData.phone))
			]);
			fb_user_data = {
				sha256_email_address: fbHashedEmail,
				sha256_phone_number: fbHashedPhone
			};
		} catch {
			fb_user_data = undefined;
		}

		if (modForm.opts.trackHashedPIIInDataLayer) {
			try {
				const [hashedFirstName, hashedLastName, hashedEmailAddress, hashedStreet] = await Promise.all([
					getHashedData(normalizeName(formData.firstName)),
					getHashedData(normalizeName(formData.lastName)),
					getHashedData(normalizeEmail(formData.email)),
					getHashedData((formData.address || '').trim().toLowerCase())
				]);
				google_user_data = {
					sha256_email_address: hashedEmailAddress,
					sha256_phone_number: fb_user_data?.sha256_phone_number,
					address: {
						sha256_first_name: hashedFirstName,
						sha256_last_name: hashedLastName,
						sha256_street: hashedStreet,
						city: (modForm.opts.zipData?.city || '').trim().toLowerCase(),
						region: (modForm.opts.zipData?.state || '').trim().toLowerCase(),
						postal_code: (formData.zip || '').trim(),
						country: (modForm.opts.zipData?.country || '').trim().toLowerCase()
					}
				};
			} catch {
				google_user_data = undefined;
			}
		}

		// Fire all conversion pixels
		if (typeof window.dataLayer !== 'undefined') {
			window.dataLayer.push({
				event: 'Homeowner Lead Conversion',
				isQSPage: 'true',
				...(google_user_data && { 'user_data': google_user_data }),
				...(fb_user_data && { fb_user_data: fb_user_data })
			});
		}

		// Track checkout in Pinterest
		if ('undefined' !== typeof pintrk) {
			pintrk('track', 'checkout', { // eslint-disable-line no-undef
				'order_id': 'undefined' !== typeof dataCaptureKey ? dataCaptureKey : null
			});
		}

		// Save response and quadlink params to cookie and redirect to the corresponding thankyou page
		cookieData = Object.assign({}, modForm.opts.quadLinkParams, responseData);

		// Set personal information
		cookieData.PROJECT = {};

		if (!isQsSiteEligibleForHrc()) {
			cookieData.PROJECT.FIRSTNAME = modForm.opts.formData.firstName;
		}

		// Append trade/vertical in cookie to get the reference in result page
		let contractorTrade = modForm.opts.contractorTrade ? modForm.opts.contractorTrade : modForm.opts.vertical;
		contractorTrade = contractorTrade.replace(/_/g, ' ').toLowerCase();
		cookieData.PROJECT.contractorTrade = contractorTrade;

		// Logic to store the matched vendorDCK in qsLeadData
		const brandedVendorKey = modForm.opts.formData && modForm.opts.formData.matchingAttributes && modForm.opts.formData.matchingAttributes.brandedVendor;
		const matchedVendors = responseData && responseData.vendors;

		if (brandedVendorKey && Array.isArray(matchedVendors) && matchedVendors.length) {
			matchedVendors.forEach((vendor) => {
				if (vendor && vendor.vendorDataCaptureKey && vendor.vendorKey && vendor.vendorKey === brandedVendorKey) {
					cookieData.vendorDCK = vendor.vendorDataCaptureKey;
				}
			});
		}

		modUtils.setCookie('qsLeadData', JSON.stringify(cookieData));

		if ('function' === typeof modForm.opts.formSubmitSuccessCallback) {
			if (modForm.opts.formSubmitSuccessCallback(responseData) === false) {
				return false;
			}
		}
		// Handling errors, conditional is when no DCK is found
	} else {
		switchFormLoadingState(false);

		if ('function' === typeof modForm.opts.formSubmitErrorCallback) {
			if (modForm.opts.formSubmitErrorCallback(responseData) === false) {
				return false;
			}
		}
	}

	// Set results page to HRC if the site is eligible, there's a DCK
	// and the current page is not a quote emerging trade page (these should not redirect to the HRC)
	if (isQsSiteEligibleForHrc() && responseData.publicLeadIdentifer && !modForm.opts.showBrandedResultPage && !modForm.opts.isBrandedExclusiveMatch) {
		window.projectId = responseData.publicLeadIdentifer;
		modForm.opts.resultsPage = getProjExUrl();
		modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + 'DCK=' + dataCaptureKey;

		if (modForm.opts.shouldSuppressConversionPixel) {
			modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + modForm.opts.suppressConversionPixelParam;
		}

		if (modForm.opts.isFirePixelTrue) {
			modForm.opts.resultsPage += (modForm.opts.resultsPage?.indexOf('?') < 0 ? '?' : '&') + modForm.opts.firePixelParam;
		}

		if (modForm.opts.isSharedMatch) {
			modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + modForm.opts.sharedParam;
		}

		if (Object.keys(modForm.opts.resultsPageAdditionalParams).length) {
			Object.entries(modForm.opts.resultsPageAdditionalParams).forEach(function([key, value]) {
				modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + key + '=' + value;
			});
		}

		if (modForm.opts.prefVendorParam && (modForm.opts.prefVendorKeys && modForm.opts.prefVendorKeys.length)) {
			modForm.opts.prefVendorParam += modForm.opts.prefVendorKeys.toString();
			modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + modForm.opts.prefVendorParam;
		}

		// Append TY to HRC URL for QS tracking purpose
		modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + (responseData.vendors && responseData.vendors.length > 0 ? 'ThankYou' : 'Sorry');

		modForm.opts.resultsPage += `&trade=${modForm.initialTrade}`;
	} else {
		// Moving this logic to centralized file where we change the /ThankYou path for /Sorry in case there were no matches
		modForm.opts.resultsPage = (responseData.vendors && responseData.vendors.length === 0) ? modForm.opts.errorRedirectPath : modForm.opts.resultsPage;

		// Append all current URL params to the results page
		if (modForm.opts.getVars && Object.keys(modForm.opts.getVars).length) {
			modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + new URLSearchParams(modForm.opts.getVars).toString();
		}
	}

	// Append TY to HRC URL for QS tracking purpose
	if (
		typeof modForm.opts.appendTyToHrcUrl !== 'undefined'
		&& modForm.opts.appendTyToHrcUrl === true
		&& modForm.opts.resultsPage.indexOf('ThankYou') < 0
		&& modForm.opts.resultsPage.indexOf('Sorry') < 0
	) {
		modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + (responseData.vendors && responseData.vendors.length > 0 ? 'ThankYou' : 'Sorry');
	}

	if (modForm.opts.isExclusiveMatch && modForm.opts.exclusiveMatchResultsPage) {
		modForm.opts.resultsPage = modForm.opts.exclusiveMatchResultsPage;
	}

	if (isWholeSaleMatch) {
		modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + 'wholesale=true';
	}

	// Adding user location info to the results page
	modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + 'userLocation=' + modForm.opts.zipCode + ',' + modForm.opts.userLocation;

	if (modForm.opts.defaultMediaChannel && !modUtils.getTrafficSource()) {
		modForm.opts.resultsPage += '&defaultMediaChannel=' + modForm.opts.defaultMediaChannel;
	}

	// Adding new HRC URL to Loader link
	if (window.Loader && modForm.opts.showLoadingOverlay) {
		window.Loader.updateLink(modForm.opts.resultsPage); // eslint-disable-line no-undef
	}

	// Renamed thankyou page to results page to be more explicit that TYP is dying
	if (modForm.opts.resultsPage && !modForm.opts.isOrganicPreferredMatch && modForm.opts.autoRedirectToTyOrSorryPage) {
		if (modForm.opts.showRedirectOverlay) {
			if (modForm.opts.isExclusiveMatch) {
				const capitalizedMatchVendor = modForm.opts.exclusiveMatchVendor.charAt(0).toUpperCase() + modForm.opts.exclusiveMatchVendor.slice(1),
					exclusiveMatchHeapMessage = capitalizedMatchVendor === 'Frontdoor' ? 'American Home Shield' : capitalizedMatchVendor;

				if (window.Loader) {
					window.Loader.showRedirectAnimation(...modForm.opts.eCommerceExclusiveLoaders[modForm.opts.exclusiveMatchVendor]); // eslint-disable-line no-undef
					Modalytics.heap('track', [`Loader Visible - Transferring to ${exclusiveMatchHeapMessage}`]);
				}
			} else if (window.Loader && modForm.opts.includeTransferringToModernize) {
				window.Loader.showRedirectAnimation(); // eslint-disable-line no-undef
				Modalytics.heap('track', ['Loader Visible - Transferring to Modernize']);
			}
			setTimeout(function() {
				setTimeout(function() {
					if (modForm.opts.isExclusiveMatch && modForm.opts.exclusiveMatchResultsPage && modForm.opts.isLeadWidget && window.parent && window.parent !== window) {
						window.parent.postMessage({ exclusiveRedirectUrl: modForm.opts.exclusiveMatchResultsPage }, '*');
						return;
					} else {
						window.location.href = modForm.opts.resultsPage;
					}
				}, 500);
				document.body.classList.add('submitted');
			}, 2500);
		} else if (modForm.opts.showLoadingOverlay) {
			document.body.classList.add('submitted');
			setTimeout(function() {
				window.location.href = modForm.opts.resultsPage;
			}, 1000);
		} else {
			window.location.href = modForm.opts.resultsPage;
		}
	}

	if (modForm.opts.isExclusiveMatch) {
		createHeapSessionIdCookie(encodeURIComponent(modForm.opts.resultsPage));
	} else {
		createHeapSessionIdCookie(modForm.opts.resultsPage);
	}
}
